// client/src/components/plataforma/siteInstitucional/ConteudosHomePage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useContents } from '../../../hooks/useContents';
import useAlert from '../../../hooks/useAlert';
import { Save, Loader2, Plus, Trash2, ListTree, Edit, Image as ImageIcon, AtSign, XCircle, MoreVertical } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabButton from '../../common/FabButton';
import ActionsDrawer from '../../common/ActionsDrawer';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';
import GerarTextoIABox from '../../ai/GerarTextoIABox';
import GerarImagemIABox from '../../ai/GerarImagemIABox';

const CONTENT_TYPES = [
    { value: 'feature', label: 'Recursos', icon: '⭐' },
    { value: 'service', label: 'Serviços', icon: '🔧' },
    { value: 'news', label: 'Notícias', icon: '📰' },
    { value: 'update', label: 'Atualizações', icon: '🔄' },
    { value: 'benefit', label: 'Benefícios', icon: '🎁' }
];

// Componente auxiliar para upload de imagem. onRemove é responsabilidade do
// pai — precisa limpar tanto o arquivo/preview local quanto editingItem.image
// (o que realmente é salvo); só limpar o estado local aqui fazia a imagem
// "removida" continuar intacta no banco, já que handleSave nunca reenvia
// editingItem.image (o upload é sempre um passo separado, opt-in).
const ImageUploader = ({ label, file, setFile, preview, setPreview, onRemove }) => {
    const handleImageChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            const previewUrl = URL.createObjectURL(selectedFile);
            setPreview(previewUrl);
        }
    };

    // Limpa o objeto URL quando o componente é desmontado ou a preview muda
    useEffect(() => {
        return () => {
            if (preview && preview.startsWith('blob:')) {
                URL.revokeObjectURL(preview);
            }
        };
    }, [preview]);

    return (
        <div className="form-group">
            <label className="font-medium">{label}</label>
            <div className="flex items-start gap-4 mt-2">
                <input type="file" accept="image/*" onChange={handleImageChange} className="form-input flex-grow" id={`image-upload-${label}`} />
                {preview && (
                    <div className="relative flex-shrink-0">
                        <img src={preview} alt="Pré-visualização" className="h-24 w-24 object-cover rounded border border-gray-200 bg-gray-50" />
                        <button type="button" onClick={onRemove} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600" title="Remover imagem">
                            <XCircle size={18} />
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

const ConteudosHomePage = () => {
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { contents, loading, createContent, updateContent, deleteContent, uploadContentImage } = useContents();
    const { setAlert } = useAlert();
    
    const [selectedType, setSelectedType] = useState('feature');
    const [isSaving, setIsSaving] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedContent, setSelectedContent] = useState(null);

    const filteredContents = contents.filter(content => content.type === selectedType);

    const handleTypeChange = useCallback((type) => {
        setSelectedType(type);
        setEditingItem(null);
    }, []);

    const handleCreateNew = useCallback(() => {
        if (!hasHomePersonalizada) return;
        setEditingItem({
            title: '',
            type: selectedType,
            subtitle: '',
            description: '',
            visualType: 'icon', // Padrão
            icon: '',
            image: '',
            status: 'draft',
            pedido: filteredContents.length
        });
        setImageFile(null);
        setImagePreview(null);
    }, [selectedType, filteredContents.length, hasHomePersonalizada]);

    const handleEdit = useCallback((content) => {
        if (!hasHomePersonalizada) return;
        setEditingItem({ ...content });
        setImageFile(null);
        setImagePreview(content.image || null);
        setSelectedContent(null);
    }, [hasHomePersonalizada]);

    const handleCancelEdit = useCallback(() => {
        setEditingItem(null);
        setImageFile(null);
        setImagePreview(null);
    }, []);

    // Precisa limpar editingItem.image também — é ele que handleSave envia
    // pro updateContent, não o estado local de arquivo/preview. Só limpar o
    // local fazia a imagem "removida" continuar salva no banco.
    const handleRemoveImage = useCallback(() => {
        setImageFile(null);
        setImagePreview(null);
        setEditingItem(prev => ({ ...prev, image: '' }));
    }, []);

    const handleSave = async (e) => {
        e.preventDefault();

        if (!hasHomePersonalizada) return;

        if (!editingItem.title || !editingItem.description) {
            setAlert('Título e descrição são obrigatórios', 'error');
            return;
        }

        setIsSaving(true);
        try {
            // Passo 1: Salva os dados de texto (cria ou atualiza)
            const contentResponse = editingItem._id
                ? await updateContent(editingItem._id, editingItem)
                : await createContent(editingItem);

            const contentId = contentResponse.data._id;

            // Se este era um "criar" e o upload da imagem falhar a seguir,
            // o _id já fica gravado no estado — um novo clique em Salvar
            // cai no ramo de updateContent acima em vez de criar outro
            // conteúdo duplicado do zero.
            if (!editingItem._id) {
                setEditingItem(prev => ({ ...prev, _id: contentId }));
            }

            // Passo 2: Se houver um novo arquivo de imagem, faz o upload
            if (imageFile) {
                await uploadContentImage(contentId, imageFile);
            }

            setAlert('Conteúdo salvo com sucesso!', 'success');
            setEditingItem(null);
            setImageFile(null);
            setImagePreview(null);
        } catch (error) {
            setAlert(error.message, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!hasHomePersonalizada) return;
        if (window.confirm('Tem certeza que deseja excluir este conteúdo?')) {
            try {
                await deleteContent(id);
                setSelectedContent(null);
            } catch (error) {
                console.error('Erro ao excluir conteúdo:', error);
            }
        }
    };

    const handleInputChange = useCallback((field, value) => {
        const isNumberField = field === 'pedido';
        setEditingItem(prev => ({
            ...prev,
            [field]: isNumberField ? parseInt(value) || 0 : value
        }));
    }, []);

    if (loading) {
        return (
            <div className="page-management-container flex justify-center items-center h-64">
                <Loader2 className="animate-spin h-8 w-8 text-blue-500" />
            </div>
        );
    }

    return (
        <div className="page-management-container">
            <PageHeader
                title="Gerenciar Conteúdos"
                icon={<ListTree className="icon-title" />}
            />

            {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

            <div className="content-panel mt-6">
                <div className="form-group mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de Conteúdo</label>
                    <div className="flex flex-wrap gap-2">
                        {CONTENT_TYPES.map(type => (
                            <button key={type.value} type="button" onClick={() => handleTypeChange(type.value)}
                                className={`px-4 py-2 rounded-lg border transition-colors ${selectedType === type.value ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                            >
                                <span className="mr-2">{type.icon}</span>{type.label}
                            </button>
                        ))}
                    </div>
                </div>

                {editingItem && (
                    <form onSubmit={handleSave} className="bg-white rounded-lg shadow-md p-6 mb-6 border border-blue-200">
                    <fieldset disabled={!hasHomePersonalizada} style={{ border: 'none', padding: 0, margin: 0 }}>
                        <h3 className="text-lg font-semibold mb-4">{editingItem._id ? 'Editar' : 'Criar'} Conteúdo</h3>

                        <GerarTextoIABox
                            endpoint="/admin/institucional/contents/gerar-ia"
                            extraPayload={{ type: editingItem.type }}
                            placeholder='Descreva o conteúdo — ex: "diferencial sobre agendamento online 24h, tom confiante"'
                            successMessage="Conteúdo gerado! Revise antes de salvar."
                            onGenerated={(data) => setEditingItem(prev => ({
                                ...prev,
                                title: data.title,
                                subtitle: data.subtitle,
                                description: data.description,
                            }))}
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-group"><label>Título *</label><input type="text" value={editingItem.title} onChange={(e) => handleInputChange('title', e.target.value)} className="form-input" placeholder="Título do conteúdo" /></div>
                            <div className="form-group"><label>Subtítulo</label><input type="text" value={editingItem.subtitle || ''} onChange={(e) => handleInputChange('subtitle', e.target.value)} className="form-input" placeholder="Subtítulo opcional" /></div>
                        </div>

                        <div className="form-group mb-4"><label>Descrição *</label><textarea value={editingItem.description} onChange={(e) => handleInputChange('description', e.target.value)} className="form-input" rows="4" placeholder="Descrição detalhada do conteúdo" /></div>

                        <div className="form-group mb-4"><label className="block text-sm font-medium text-gray-700 mb-2">Representação Visual</label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="visualType" value="icon" checked={editingItem.visualType === 'icon'} onChange={() => handleInputChange('visualType', 'icon')} /> <AtSign size={16} className="text-gray-600"/> Ícone</label>
                                <label className="flex items-center gap-2 cursor-pointer"><input type="radio" name="visualType" value="image" checked={editingItem.visualType === 'image'} onChange={() => handleInputChange('visualType', 'image')} /> <ImageIcon size={16} className="text-gray-600"/> Imagem</label>
                            </div>
                        </div>
                        
                        {editingItem.visualType === 'icon' ? (
                            <div className="form-group mb-4"><label>Ícone (ex: fas fa-star)</label><input type="text" value={editingItem.icon || ''} onChange={(e) => handleInputChange('icon', e.target.value)} className="form-input" /></div>
                        ) : (
                            <div className="mb-4">
                                <ImageUploader label="Imagem do Conteúdo" file={imageFile} setFile={setImageFile} preview={imagePreview} setPreview={setImagePreview} onRemove={handleRemoveImage} />
                                <GerarImagemIABox
                                    aspectRatio="3:2"
                                    aspectHint="Tamanho ideal: 360×220px, paisagem. A imagem aparece cortada pra preencher o card na home institucional — evite fotos na vertical."
                                    placeholder='Ex: "ilustração moderna de agendamento online, cores azul e branco, estilo flat design"'
                                    onUseImage={(file, previewUrl) => { setImageFile(file); setImagePreview(previewUrl); }}
                                />
                            </div>
                        )}

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div className="form-group"><label>Ordem</label><input type="number" value={editingItem.pedido || 0} onChange={(e) => handleInputChange('pedido', e.target.value)} className="form-input" /></div>
                            <div className="form-group"><label>Status</label><select value={editingItem.status} onChange={(e) => handleInputChange('status', e.target.value)} className="form-input"><option value="draft">Rascunho</option><option value="published">Publicado</option></select></div>
                        </div>

                        <div className="flex gap-2"><button type="submit" disabled={isSaving} className="action-button action-button-primary">{isSaving ? <Loader2 size={16} className="icon animate-spin" /> : <Save size={16} className="icon" />} {editingItem._id ? 'Atualizar' : 'Criar'}</button><button type="button" onClick={handleCancelEdit} disabled={isSaving} className="action-button action-button-secondary">Cancelar</button></div>
                    </fieldset>
                    </form>
                )}

                <div>
                    <h3 className="text-lg font-semibold mb-4">Conteúdos de {CONTENT_TYPES.find(t => t.value === selectedType)?.label} <span className="text-sm text-gray-500 ml-2">({filteredContents.length} itens)</span></h3>
                    {filteredContents.length === 0 ? (<div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">Nenhum conteúdo encontrado para este tipo.</div>) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {filteredContents.map(content => (
                                <div key={content._id} className="border rounded-lg p-4 bg-white shadow-sm flex flex-col">
                                    <div className="flex items-start justify-between mb-3">
                                        <h4 className="font-semibold text-gray-800 break-words">{content.title}</h4>
                                        <span className={`text-xs px-2 py-1 rounded-full flex-shrink-0 ${content.status === 'published' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>{content.status === 'published' ? 'Publicado' : 'Rascunho'}</span>
                                    </div>
                                    {content.subtitle && (<p className="text-sm text-gray-600 mb-3">{content.subtitle}</p>)}
                                    <p className="text-xs text-gray-500 mb-3 line-clamp-2 flex-grow">{content.description}</p>
                                    <div className="flex items-center justify-between mt-auto pt-3 border-t">
                                        <span className="text-xs text-gray-500">Ordem: {content.pedido}</span>
                                        <button onClick={() => setSelectedContent(content)} className="action-icon-button text-gray-600" title="Ações" disabled={!hasHomePersonalizada}><MoreVertical size={16} /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <ActionsDrawer
                open={!!selectedContent}
                onClose={() => setSelectedContent(null)}
                title={selectedContent?.title || 'Conteúdo'}
                actions={selectedContent ? [
                    {
                        key: 'editar',
                        label: 'Editar',
                        icon: <Edit size={20} className="icon" />,
                        onClick: () => handleEdit(selectedContent),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedContent._id),
                        destructive: true,
                    },
                ] : []}
            />

            <FabButton onClick={handleCreateNew} title="Novo Conteúdo" icon={<ListTree size={26} className="icon" />} disabled={isSaving || !!editingItem || !hasHomePersonalizada} />
        </div>
    );
};

export default ConteudosHomePage;